// リソース宣言（Layer 1）の型。
//
// 【設計】新しいエンティティを AI秘書に対応させる ＝ ResourceDescriptor を1つ書いて
// index.ts の RESOURCES に足すだけ。ツール（Layer 2）は増えない。
// 詳細: docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §5
import type { z } from 'zod';
import type { CustomerRank, CustomerStatus, ID } from '@shared/types';

export const RESOURCE_NAMES = [
  'quote',
  'customer',
  'order',
  'delivery',
  'invoice',
  'purchase_order',
  'subcontract_order',
  'procurement_order',
] as const;
export type ResourceName = (typeof RESOURCE_NAMES)[number];

export const CUSTOMER_STATUS = ['active', 'inactive', 'prospect'] as const satisfies readonly CustomerStatus[];
export const CUSTOMER_RANK = ['SSS', 'SS', 'S', 'A', 'B', 'C'] as const satisfies readonly CustomerRank[];

/**
 * list_records の共通フィルタ。
 * 【意図的にフラット】小型モデルはネストした引数を組むのが苦手なため、
 * リソース別に入れ子のfiltersを持たせず、全リソースのフィルタを平らに並べる。
 * 各リソースは自分に関係する項目だけを読み、残りは無視する。
 */
export interface ListFilters {
  /** 名称の部分一致（quote=顧客名 / customer=会社名・カナ） */
  keyword?: string;
  /** YYYY-MM。quote のみ解釈（作成月で絞る） */
  month?: string;
  /** customer のみ解釈 */
  status?: CustomerStatus;
  /** customer のみ解釈 */
  rank?: CustomerRank;
}

/** describe_resource が LLM へ返す「書ける項目」1件。 */
export interface WriteField {
  name: string;
  label: string;
  type: 'string' | 'date' | 'ref' | 'enum' | 'items';
  required?: boolean;
  /** type='ref' のとき参照先リソース（例 customer）。 */
  resource?: ResourceName;
  /** type='enum' のとき選べる値。 */
  values?: readonly string[];
  /** LLM 向けの補足（値の作り方・形式）。 */
  note?: string;
}

/** 書込の実行結果。チャットの結果カードはこの値から「システム側が」描画する。 */
export interface WriteResult {
  id: ID;
  code: string;
}

/**
 * 書込宣言。fields は「AIに許す項目」の唯一のポリシー面で、
 * 本体スキーマが受け付ける項目の常に部分集合（設計書 §5）。
 * 本体の zod は createXxx/updateXxx 内で最終検証として依然走る（多層防御）。
 */
export interface ResourceWriteSpec {
  fields: WriteField[];
  /** create_record の data 検証（fields に対応する zod）。 */
  createSchema: z.ZodTypeAny;
  /** update_record の changes 検証（部分更新なので全て任意）。 */
  updateSchema: z.ZodTypeAny;
  /** 作成を実行し、作られたレコードの id/code を返す。aiPendingReview は実装側で必ず立てる。 */
  create: (data: Record<string, unknown>, userId: ID) => Promise<WriteResult>;
  /** code で対象を解決して更新。見つからなければ null。 */
  update: (code: string, changes: Record<string, unknown>, userId: ID) => Promise<WriteResult | null>;
  /** 作成/更新したレコードを開く画面のパス（結果カードのリンク先）。 */
  link: (id: ID) => string;
}

/** リソース1件ぶんの宣言。write が無いリソースは読取専用。 */
export interface ResourceDescriptor {
  name: ResourceName;
  label: string;
  /** list_records の実行。使わないフィルタは無視してよい */
  list: (filters: ListFilters) => Promise<unknown>;
  /** get_record の実行。code は生値（表記ゆれの正規化は各リソースの責務） */
  get: (code: string) => Promise<unknown>;
  /** 書込対応（C-2）。宣言されたリソースだけが create/update_record の対象になる */
  write?: ResourceWriteSpec;
}
